import type { ScopeContext } from '@sentry/types';

const ERROR_OPTIONS = new WeakMap<Error, ReportExceptionOptions>();

/**
 * File to attach to the exception in Sentry.
 */
export interface ExceptionAttachment {
    /** File name */
    name: string;
    /** Body as a string for the file */
    body: string | Uint8Array;
    /** Mime-type for the attachment */
    type?: string;
}

/**
 * Options to attach to the exception in Sentry.
 */
export type ReportExceptionOptions = Partial<
    Pick<ScopeContext, 'tags' | 'extra' | 'fingerprint' | 'level'>
> & {
    /** Attachments for the exception */
    attachments?: ExceptionAttachment[];
};

/**
 * A wrapped error allowing the called to add tags and metadata ready for Sentry.
 */
export class ReportException extends Error {
    public options: ReportExceptionOptions;

    constructor(message: string, options?: ReportExceptionOptions) {
        super(message);

        this.options = options || {};
    }

    /**
     * Annotate an error with additional metadata.
     */
    public static annotateError(error: Error, options: ReportExceptionOptions): ReportException {
        const reportError = new ReportException(error.message, options);
        reportError.stack = error.stack || error.message;
        return reportError;
    }

    /** Attach reporting context without replacing or mutating the error. */
    public static attachOptions<T extends Error>(error: T, options: ReportExceptionOptions): T {
        ERROR_OPTIONS.set(error, options);
        return error;
    }

    public static getOptions(error: unknown): ReportExceptionOptions | undefined {
        if (!(error instanceof Error)) {
            return undefined;
        }
        return (
            ERROR_OPTIONS.get(error) ??
            (error instanceof ReportException ? error.options : undefined)
        );
    }
}
